import { useCallback } from 'react'
import { useMutation, useQueryClient } from 'react-query'

import type { NotificationsService } from 'features/notifications/service'

export interface UseNotificationMutationsResult {
  markManyAsRead: (ids: number[]) => Promise<void>
  markManyAsUnread: (ids: number[]) => Promise<void>
  deleteMany: (ids: number[]) => Promise<void>
  isMutating: boolean
}

/**
 * Sub-hook owning the notification write mutations. Private to the
 * `use-list-notifications` orchestrator — consumers go through it.
 *
 * Bulk shapes over the per-id endpoints: the API is per-notification
 * (PUT …/read, PUT …/unread, DELETE …), so a selection fans out into N
 * requests. `allSettled` keeps one failed row from blocking the rest;
 * the single settle-then-invalidate refetch reconciles whatever landed.
 */
export function useNotificationMutations(
  service: NotificationsService,
): UseNotificationMutationsResult {
  const queryClient = useQueryClient()

  const onMutationSettled = useCallback(
    () => queryClient.invalidateQueries(['notifications', 'list']),
    [queryClient],
  )

  const { mutateAsync: markReadMutation, isLoading: isMarkingRead } =
    useMutation(
      (ids: number[]) =>
        Promise.allSettled(ids.map((id) => service.markAsRead(id))),
      { onSettled: onMutationSettled },
    )

  const { mutateAsync: markUnreadMutation, isLoading: isMarkingUnread } =
    useMutation(
      (ids: number[]) =>
        Promise.allSettled(ids.map((id) => service.markAsUnread(id))),
      { onSettled: onMutationSettled },
    )

  const { mutateAsync: deleteMutation, isLoading: isDeleting } = useMutation(
    (ids: number[]) =>
      Promise.allSettled(ids.map((id) => service.deleteNotification(id))),
    { onSettled: onMutationSettled },
  )

  const markManyAsRead = useCallback(
    async (ids: number[]): Promise<void> => {
      await markReadMutation(ids)
    },
    [markReadMutation],
  )

  const markManyAsUnread = useCallback(
    async (ids: number[]): Promise<void> => {
      await markUnreadMutation(ids)
    },
    [markUnreadMutation],
  )

  const deleteMany = useCallback(
    async (ids: number[]): Promise<void> => {
      await deleteMutation(ids)
    },
    [deleteMutation],
  )

  return {
    markManyAsRead,
    markManyAsUnread,
    deleteMany,
    isMutating: isMarkingRead || isMarkingUnread || isDeleting,
  }
}
